import { useEffect, useState, useCallback, useRef } from 'react';
import type { SiteTreeNode } from '../services/siteManagement/siteManagement.types';
import * as siteService from '../services/siteManagement/siteManagement.service';

// Helper to recursively process tree nodes and filter VT nodes, keeping only valid VTs
const processTreeNodes = (treeNodes: SiteTreeNode[] | undefined, validVtIds: Set<number>): SiteTreeNode[] | undefined => {
  if (!treeNodes) return undefined;
  return treeNodes
    .filter(node => {
      // Keep all non-VT nodes
      if (!String(node.key || '').startsWith('vt-')) {
        return true;
      }
      // For VT nodes, keep only if the ID is in the valid set
      const vtIdStr = String(node.key).replace('vt-', '');
      const vtId = Number(vtIdStr);
      const isValid = !Number.isNaN(vtId) && validVtIds.has(vtId);
      return isValid;
    })
    .map(node => ({
      ...node,
      // Recursively process children at all levels
      children: processTreeNodes(node.children, validVtIds)
    }));
};

// Helper to inject VTs from VT list that are missing in the tree structure
// This handles cases where backend tree endpoint is stale/cached
type VirtualTenantCandidate = {
  id?: number | string;
  parentSiteHierarchyId?: number | null;
  name?: string | null;
  siteIdentifier?: string | null;
  virtualTenantIdentifier?: string | null;
  siteIdentifierRegistryId?: number | null;
  isActive?: boolean | null;
};

const injectMissingVTs = (treeNodes: SiteTreeNode[], vtList: VirtualTenantCandidate[], validVtIds: Set<number>): SiteTreeNode[] => {
  // Build a map of existing VT IDs in the tree
  const existingVtIds = new Set<number>();
  const collectExistingVTs = (nodes: SiteTreeNode[] | undefined) => {
    if (!nodes) return;
    nodes.forEach(node => {
      if (String(node.key || '').startsWith('vt-')) {
        const vtId = Number(String(node.key).replace('vt-', ''));
        if (!Number.isNaN(vtId)) existingVtIds.add(vtId);
      }
      collectExistingVTs(node.children);
    });
  };
  collectExistingVTs(treeNodes);

  // Find VTs that exist in the list but not in the tree
  const missingVTs = vtList.filter(vt => {
    const vtId = Number(vt.id);
    return !Number.isNaN(vtId) && validVtIds.has(vtId) && !existingVtIds.has(vtId);
  });

  if (missingVTs.length === 0) {
    return treeNodes;
  }


  // Recursively inject missing VTs into their parent site nodes
  const injectIntoTree = (nodes: SiteTreeNode[] | undefined): SiteTreeNode[] | undefined => {
    if (!nodes) return undefined;
    return nodes.map(node => {
      const processedNode = { ...node };
      
      // If this is a site node, check if any missing VTs belong to it
      if (String(node.key || '').startsWith('site-')) {
        const siteShId = node.shId; // Site hierarchy ID
        const vtsForThisSite = missingVTs.filter(vt => vt.parentSiteHierarchyId === siteShId);
        
        if (vtsForThisSite.length > 0) {
          
          // Create VT nodes
          const vtNodes: SiteTreeNode[] = vtsForThisSite.map(vt => ({
            key: `vt-${vt.id}`,
            title: vt.name || vt.siteIdentifier || `VT-${vt.id}`,
            type: 'vt',
            customerId: null,
            sirId: vt.siteIdentifierRegistryId || null,
            shId: Number(vt.id),
            isActive: vt.isActive ?? true,
            statusName: null,
            siteIdentifier: vt.virtualTenantIdentifier || vt.siteIdentifier || null,
            siteCount: null,
            vtCount: null,
            children: []
          }));
          
          // Add to children
          processedNode.children = [...(node.children || []), ...vtNodes];
        }
      }
      
      // Recursively process children
      processedNode.children = injectIntoTree(processedNode.children);
      return processedNode;
    });
  };

  return injectIntoTree(treeNodes) || treeNodes;
};

// Supplement the base tree with per-customer sites.
// The global tree call (no customerId) returns a subset of sites — some sites with active
async function supplementTreeWithCustomerSites(
  tree: SiteTreeNode[],
  validVtIds: Set<number>
): Promise<SiteTreeNode[]> {
  const customerNodes = tree.filter(n => n.type === 'customer' && n.customerId != null);
  if (customerNodes.length === 0) {
    return tree;
  }

  const customerIds = customerNodes.map(n => Number(n.customerId));
  const responses = await Promise.allSettled(
    customerIds.map(id => siteService.listSiteTree(id))
  );

  const result = [...tree];
  responses.forEach((resp, idx) => {
    const custNode = customerNodes[idx];
    const customerId = custNode.customerId;
    
    if (resp.status !== 'fulfilled' || !resp.value?.success) {
      return;
    }
    
    const custData: SiteTreeNode[] = resp.value.data ?? [];
    const custRoot = custData.find(n => n.type === 'customer' && n.customerId === custNode.customerId);
    if (!custRoot || !Array.isArray(custRoot.children)) {
      return;
    }
    
    const existingKeys = new Set((custNode.children ?? []).map(c => c.key));
    const missingSites = custRoot.children.filter(s => !existingKeys.has(s.key));
    
    if (missingSites.length === 0) {
      return;
    }
    
    const processedMissing = processTreeNodes(missingSites, validVtIds) ?? missingSites;
    const treeIdx = result.findIndex(n => n.key === custNode.key);
    if (treeIdx >= 0) {
      result[treeIdx] = {
        ...result[treeIdx],
        children: [...(result[treeIdx].children ?? []), ...processedMissing]
      };
    }
  });

  return result;
}

export function useSiteTree() {
  const [nodes, setNodes] = useState<SiteTreeNode[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const fetchCounterRef = useRef<number>(0);
  const isFetchingRef = useRef<boolean>(false);

  const doFetch = useCallback(async () => {
    isFetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      // Fetch both tree and VT list in parallel
      const [treeRes, vtRes] = await Promise.allSettled([
        siteService.listSiteTree(),
        siteService.listVirtualTenants()
      ]);

      if (treeRes.status !== 'fulfilled' || !treeRes.value.success) {
        const errMsg = treeRes.status === 'rejected' ? 'Failed to load tree' : treeRes.value.error?.message || 'Failed to load site tree';
        setError(errMsg);
        return;
      }

      // Build a set of valid VT IDs from the VT list
      let validVtIds = new Set<number>();
      let vtListData: VirtualTenantCandidate[] = [];
      if (vtRes.status === 'fulfilled' && vtRes.value.success && Array.isArray(vtRes.value.data)) {
        vtListData = vtRes.value.data as VirtualTenantCandidate[];
        validVtIds = new Set(vtListData.map(vt => Number(vt.id)).filter((id: number) => !Number.isNaN(id)));
      }

      // Process the entire tree recursively to filter VTs at all levels
      let filteredTree = processTreeNodes(treeRes.value.data || undefined, validVtIds) || [];

      // Inject missing VTs that exist in VT list but not in tree structure
      filteredTree = injectMissingVTs(filteredTree, vtListData, validVtIds);

      // Supplement base tree with per-customer sites.
      filteredTree = await supplementTreeWithCustomerSites(filteredTree, validVtIds);

      setNodes(filteredTree);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  // Fetch tree on initial mount only
  useEffect(() => {
    doFetch();
  }, []);

  // Return a refresh function that waits for the fetch to complete
  const refresh = useCallback(async () => {
    await doFetch();
  }, [doFetch]);

  return { nodes, loading, error, refresh };
}

export default useSiteTree;
